import { useListener } from '#~/command/hooks.js';
import { Command } from '#~/command/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { useDeviceIds, useRoom } from '#~/room/hooks.js';
import { Content } from '@studio/design-system/tabs';
import { toAccessor } from '@studio/react-utils';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import { Sources as AudioSources } from '../audio/sources/index.js';
import { Sources as VideoSources } from '../video/sources/index.js';
import { Add } from './add.jsx';
import { useLeftTabPref } from './hooks.js';
import { Root } from './root.jsx';
import { Triggers } from './triggers.jsx';
import type { Props } from './types.js';
import { Tab } from './types.js';

/** `<LeftTray>` component. */
export const LeftTray = (props: Props): ReactElement => {
  const [tab, setTab] = useLeftTabPref();
  const { audioIds, videoIds } = useDeviceIds();

  // Command listener.
  useListener(({ command }) => {
    if (command === Command.TRAY_LEFT_TAB) {
      setTab(tab === Tab.Video ? Tab.Audio : Tab.Video);
    }
  });

  const roomId = useRoom((µ) => µ.id);

  const canControl = useDepot((u) => u.roomRecords[roomId]?.permissions.canControl);

  return (
    <Root {...props} tab={toAccessor([tab, setTab])}>
      <Triggers audioCount={audioIds.length} videoCount={videoIds.length}>
        {!!canControl && <Add className='ml-auto self-center' />}
      </Triggers>

      <Content value={Tab.Video}>
        {tab === Tab.Video && <VideoSources canControl={!!canControl} />}
      </Content>
      <Content
        className={twMerge(tab !== Tab.Audio && 'absolute h-[1px] w-[1px] overflow-hidden')}
        forceMount
        value={Tab.Audio}
      >
        <AudioSources canControl={!!canControl} />
      </Content>
    </Root>
  );
};
